/**
 * ATTENTION: read this if you are adding a new param:
 * 1. Add the param key to ALLOWED_KEYS. (or ALLOWED_PREFIXES if it can be
 *    partially matched)
 * 2. Add the param key to selected groups if it has special values:
 *    a. boolean => BOOL_VALUE_KEYS
 *    b. int => INT_VALUE_KEYS
 *    c. empty value allowed => EMPTY_VALUE_KEYS
 */
import { isFalsy } from '@embed/helpers';
/** Keys that are allowed. */
const ALLOWED_KEYS = [
    'a11y_disable_focus',
    'a11y_disable_previews',
    'api_host',
    'api_host_ads',
    'api_host_tracking',
    'api_host_tracking_legacy',
    'app_id',
    'autoplay',
    'pip_navigate',
    'branding',
    'captions',
    'channel',
    'channel_id',
    'collapsed',
    'dir',
    'dock',
    'hashtag',
    'height',
    'hide_show_detail',
    'homepage_url',
    'horizontal',
    'in_sdk_webview',
    'language',
    'loop',
    'lsdesktop',
    'max_videos',
    'minimized',
    'mode',
    'name',
    'open_in',
    'page_type',
    'page_url',
    'paginate_by',
    'per_page',
    'pip',
    'pip_target',
    'placement',
    'player_margin',
    'player_minimize',
    'player_placement',
    'playlist',
    'playlist_collection',
    'reset_transform',
    'router',
    'screen',
    'share',
    'share_template',
    'show_preroll',
    'size',
    'start_time',
    'target',
    'text',
    'title',
    'type',
    'ui_border_style',
    'ui_button_color',
    'ui_button_font_color',
    'ui_font_css',
    'ui_primary_color',
    'ui_share_sheet',
    'vast_attrs',
    'vertical',
    'video',
];
/**
 * Keys with certain prefix are allowed.
 * @example
 * <fw-storyblock cohort_store="san mateo"></fw-storyblock>
 */
const ALLOWED_PREFIXES = ['cohort_'];
/**
 * Keys to be kept even if their value is empty.
 *
 * @example
 * <fw-storyblock title></fw-storyblock>
 */
const EMPTY_VALUE_KEYS = [
    'max_videos',
    'router',
    'show_preroll',
    'title',
    'ui_share_sheet',
];
/**
 * Keys representing boolean attributes. Their string values will be converted
 * to boolean.
 *
 * @example
 * <fw-storyblock autoplay></fw-storyblock>
 * `autoplay` value is set to true.
 */
const BOOL_VALUE_KEYS = [
    'a11y_disable_focus',
    'pip_navigate',
    'autoplay',
    'branding',
    'captions',
    'dock',
    'hide_show_detail',
    'in_sdk_webview',
    'loop',
    'minimized',
    'pip',
    'player_minimize',
];
/**
 * Keys whose values are integers.
 *
 * @example
 * <fw-storyblock paginate_by="3"></fw-storyblock>
 * `paginate_by` value is set to number 3.
 */
const INT_VALUE_KEYS = ['max_videos', 'paginate_by', 'start_time'];
/**
 * Validate single widget param.
 *
 * @param param Param set by publishers from widget attributes.
 * @returns Boolean if param is allowed.
 */
export const allowWidgetParam = (param) => ALLOWED_KEYS.includes(param) ||
    ALLOWED_PREFIXES.some((prefix) => param.startsWith(prefix));
/**
 * @param widgetParams Params set by publishers from widget attributes.
 * @returns The sanitized widget params.
 */
export const sanitizeWidgetParams = (widgetParams) => {
    return (Object.entries(widgetParams)
        // Filter allowed params
        .filter(([key]) => allowWidgetParam(key))
        // Convert numeric strings to integers
        .map(([key, value]) => INT_VALUE_KEYS.includes(key)
        ? [key, parseInt(value !== null && value !== void 0 ? value : '0', 10)]
        : [key, value])
        // Convert falsy strings to booleans
        .map(([key, value]) => BOOL_VALUE_KEYS.includes(key)
        ? [key, !isFalsy(value)]
        : [key, value])
        // Keep only nonempty params, unless key in exceptions
        .filter(([key, value]) => EMPTY_VALUE_KEYS.includes(key) || value !== '')
        .reduce((acc, [key, value]) => (Object.assign(Object.assign({}, acc), { [key]: value })), {}));
};
